import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from './utils';

/**
 * Card — styled per Pelipost UX/UI Style Guide §7.
 *
 *   default (padded)     Standard elevated card: white bg, 16px radius, md shadow, 16px padding
 *   listItem             White bg, 12px radius, 16px padding, 1px border, NO shadow (use inside scroll lists)
 *   selectedCta          List/selection card in selected state — 2px CTA border, CTA-light bg (facility selection)
 *   selectedBrand        List/selection card in selected state — 2px brand border (contact selection with checkmark)
 *   jurisdiction         List-item variant with sm shadow (style guide §7 "Jurisdiction Card")
 *   unpadded             Default shell without built-in padding (for custom layouts)
 *
 * Use the Card component for all elevated containers per style guide §17 ("Do's").
 */
const cardVariants = cva('bg-surface text-text', {
  variants: {
    variant: {
      default: 'rounded-[16px] shadow-md',
      listItem: 'rounded-[12px] border border-border',
      selectedCta: 'rounded-[16px] border-2 border-cta bg-cta-light',
      selectedBrand: 'rounded-[16px] border-2 border-brand',
      jurisdiction: 'rounded-[16px] border border-border shadow-sm',
      unpadded: 'rounded-[16px] shadow-md',
    },
    padded: {
      true: 'p-4', // 16px = spacing.lg per style guide §7
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    padded: true,
  },
});

type CardProps = React.ComponentProps<'div'> & VariantProps<typeof cardVariants>;

function Card({ className, variant, padded, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ variant, padded, className }))}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn('flex flex-col gap-1.5 pb-3', className)}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'h3'>) {
  return (
    <h3
      data-slot="card-title"
      className={cn('text-lg font-semibold leading-tight text-text', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="card-description"
      className={cn('text-sm text-text-light', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn(className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center pt-3', className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  cardVariants,
};
